import { treePositionAt } from "./PickerTree";
import type { PickerTreePosition } from "./Picker.types";

/** Where a composed row sits in the tree: the `Picker.Item` connector props. */
export interface PickerTreeRowPlacement {
    /** Absent at depth 0, which draws no connector. */
    treePosition?: PickerTreePosition;
    ancestorLines: readonly number[];
}

/**
 * Tree connector placement for a composed list: given the depths of a
 * pre-ordered flat list of visible rows, each row's `treePosition` among its
 * siblings and the `ancestorLines` running through it, by the same rules
 * `flattenItems` applies to data-driven rows (an ancestor's line continues
 * while it has a later sibling). Feed the result to `Picker.Item`'s
 * `depth` / `treePosition` / `ancestorLines`.
 */
export const pickerTreeRowPlacements = (
    depths: readonly number[],
): PickerTreeRowPlacement[] => {
    // Rows sharing a parent form a sibling group; `openGroups[depth]` is the
    // group still accepting rows at that depth.
    const groupSizes: number[] = [];
    const openGroups: number[] = [];
    const lastRowOfGroup: number[] = [];
    const groupOfRow: number[] = [];
    const indexInGroup: number[] = [];
    // Each row's ancestor rows at depths 1 .. depth - 1.
    const ancestorsOfRow: Array<Array<number | undefined>> = [];

    depths.forEach((depth, index) => {
        openGroups.length = Math.min(openGroups.length, depth + 1);
        ancestorsOfRow.push(
            Array.from({ length: Math.max(depth - 1, 0) }, (_, level) => {
                const ancestorGroup = openGroups[level + 1];
                return ancestorGroup === undefined
                    ? undefined
                    : lastRowOfGroup[ancestorGroup];
            }),
        );
        let group = openGroups[depth];
        if (group === undefined) {
            group = groupSizes.push(0) - 1;
            openGroups[depth] = group;
        }
        groupOfRow.push(group);
        indexInGroup.push(groupSizes[group]);
        groupSizes[group] += 1;
        lastRowOfGroup[group] = index;
    });

    const positionOf = (index: number) =>
        treePositionAt(indexInGroup[index], groupSizes[groupOfRow[index]]);
    const isLineContinuing = (index: number | undefined) => {
        if (index === undefined) {
            return false;
        }
        const position = positionOf(index);
        return position === "top" || position === "middle";
    };

    return depths.map((depth, index) => {
        if (depth === 0) {
            return { ancestorLines: [] };
        }
        return {
            treePosition: positionOf(index),
            ancestorLines: ancestorsOfRow[index].flatMap((ancestor, level) =>
                isLineContinuing(ancestor) ? [level + 1] : [],
            ),
        };
    });
};
